import { useState, useEffect, useRef } from 'react'
import i18n from '../../../i18n'
import { bruktbilfinansieringAPI } from '../../../services/api'
import type { TransferClassesResponse } from '../../../types/bruktbilfinansiering'
import type { BruktbilfinansieringFilters } from './useBruktbilfinansiering'

const CLASSES_DEBOUNCE_MS = 350

export function useTransferClasses(
  bankOrgNr: string,
  filters: BruktbilfinansieringFilters,
  selectedClasses: string[],
) {
  const [data, setData] = useState<TransferClassesResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetch = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await bruktbilfinansieringAPI.getTransferClasses(bankOrgNr, {
        start_date: filters.startDate,
        end_date: filters.endDate,
        dealer_postnummer: filters.dealerPostnummer ?? undefined,
        buyer_postnummer: filters.buyerPostnummer ?? undefined,
        varebil: filters.varebil ?? undefined,
        classes: selectedClasses.length ? selectedClasses : undefined,
      })
      setData(res.data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : i18n.t('errors.unknown', { ns: 'bbf' }))
    } finally {
      setLoading(false)
    }
  }

  // Immediate fetch when global filters change (bank, dates, geo, varebil).
  useEffect(() => {
    refetch()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankOrgNr, filters.startDate, filters.endDate, filters.dealerPostnummer, filters.buyerPostnummer, filters.varebil])

  // Debounced fetch when class selection changes — rapid checkbox toggles
  // would otherwise fire one API call per click.
  const isFirstClassRender = useRef(true)
  useEffect(() => {
    if (isFirstClassRender.current) {
      isFirstClassRender.current = false
      return
    }
    const t = setTimeout(() => refetch(), CLASSES_DEBOUNCE_MS)
    return () => clearTimeout(t)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedClasses)])

  return { data, loading, error, refetch }
}
